<script lang="ts">
  import { onMount } from 'svelte'
  import { api, type ApiToken } from '../lib/api'
  import { fmtRelative, copyToClipboard } from '../lib/utils'
  import { notify } from '../lib/toast'
  import Modal from '../components/Modal.svelte'

  let tokens = $state<ApiToken[]>([])
  let forbidden = $state(false)
  let addOpen = $state(false), helpOpen = $state(false)
  let newName = $state(''), newRole = $state<ApiToken['role']>('editor')
  let created = $state<{ name: string; token: string } | null>(null)

  const roleLabels: Record<string, string> = { viewer: 'только чтение', editor: 'редактор', admin: 'администратор' }
  const base = location.origin

  async function load() {
    try {
      tokens = await api.listApiTokens()
      forbidden = false
    } catch (e: any) {
      if (e.status === 403) forbidden = true
      else notify(e.message, 'err')
    }
  }
  onMount(load)

  async function handleCreate() {
    if (!newName.trim()) return notify('Укажите название токена', 'err')
    try {
      const r = await api.createApiToken({ name: newName.trim(), role: newRole })
      created = { name: r.name, token: r.token }
      addOpen = false; newName = ''; newRole = 'editor'
      await load()
    } catch (e: any) { notify(e.message, 'err') }
  }

  async function toggle(t: ApiToken) {
    try {
      await api.updateApiToken(t.id, { enabled: !t.enabled })
      notify(t.enabled ? 'Токен отключён' : 'Токен включён', 'ok')
      await load()
    } catch (e: any) { notify(e.message, 'err') }
  }

  async function remove(t: ApiToken) {
    if (!confirm(`Удалить токен «${t.name}»? Интеграции, которые его используют, перестанут работать.`)) return
    try { await api.deleteApiToken(t.id); notify('Удалено', 'ok'); await load() }
    catch (e: any) { notify(e.message, 'err') }
  }

  async function copy(text: string) {
    if (await copyToClipboard(text)) notify('Скопировано', 'ok')
  }

  const exampleDomain = (tok: string) => `curl -X POST ${base}/api/domains \\
  -H "Authorization: Bearer ${tok}" \\
  -H "Content-Type: application/json" \\
  -d '{"name": "example.com", "category": "youtube"}'`

  const exampleBulk = (tok: string) => `curl -X POST ${base}/api/domains/bulk \\
  -H "Authorization: Bearer ${tok}" \\
  -H "Content-Type: application/json" \\
  -d '{"names": ["a.example.com", "b.example.com"], "category": "youtube"}'`

  const exampleIp = (tok: string) => `curl -X POST ${base}/api/ip-ranges \\
  -H "Authorization: Bearer ${tok}" \\
  -H "Content-Type: application/json" \\
  -d '{"address": "203.0.113.0/24", "group": "telegram", "comment": "DC5"}'`

  const exampleIpBulk = (tok: string) => `curl -X POST ${base}/api/ip-ranges/bulk \\
  -H "Authorization: Bearer ${tok}" \\
  -H "Content-Type: application/json" \\
  -d '{"addresses": ["203.0.113.0/24", "198.51.100.7"], "group": "telegram"}'`
</script>

<div class="card mb-4">
  <div class="flex justify-between items-center gap-2 flex-wrap">
    <div>
      <h2 class="text-base font-semibold">API-токены</h2>
      <p class="text-xs text-muted mt-1">
        Доступ к REST API по заголовку <code>Authorization: Bearer vr_…</code> — для скриптов и интеграций, которые добавляют домены и IP.
      </p>
    </div>
    <div class="flex gap-2">
      <button class="btn btn-secondary" onclick={() => (helpOpen = true)}>Как использовать</button>
      {#if !forbidden}<button class="btn btn-primary" onclick={() => (addOpen = true)}>+ Токен</button>{/if}
    </div>
  </div>

  {#if forbidden}
    <p class="text-sm text-muted mt-4">Управление API-токенами доступно только администраторам.</p>
  {:else if tokens.length === 0}
    <p class="text-sm text-muted mt-4">Токенов пока нет. Создайте первый, чтобы добавлять домены и IP через API.</p>
  {:else}
    <div class="mt-4 overflow-x-auto">
      <table class="tbl">
        <thead>
          <tr>
            <th>Название</th>
            <th>Токен</th>
            <th>Роль</th>
            <th>Статус</th>
            <th>Использован</th>
            <th>Создан</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {#each tokens as t (t.id)}
            <tr>
              <td class="font-medium">{t.name}</td>
              <td><code class="text-xs font-mono text-blue-300">{t.prefix}…</code></td>
              <td><span class="badge badge-{t.role==='admin'?'green':t.role==='editor'?'blue':'neutral'}">{roleLabels[t.role] || t.role}</span></td>
              <td>
                {#if t.enabled}<span class="badge badge-green">активен</span>{:else}<span class="badge badge-red">отключён</span>{/if}
              </td>
              <td class="text-xs text-muted whitespace-nowrap">
                {#if t.lastUsedAt}{fmtRelative(t.lastUsedAt)} · {t.useCount}×{:else}—{/if}
              </td>
              <td class="text-xs text-muted whitespace-nowrap">{fmtRelative(t.createdAt)}{#if t.createdBy} · @{t.createdBy}{/if}</td>
              <td class="whitespace-nowrap text-right">
                <button class="btn-icon" onclick={() => toggle(t)} title={t.enabled ? 'Отключить' : 'Включить'}>{t.enabled ? '⏸' : '▶'}</button>
                <button class="btn-icon btn-icon-danger" onclick={() => remove(t)} title="Удалить">🗑</button>
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  {/if}
</div>

{#if addOpen}
  <Modal title="Новый API-токен" onclose={() => (addOpen = false)}>
    <div class="space-y-3">
      <div>
        <label class="label">Название</label>
        <input class="input" bind:value={newName} placeholder="n8n / мониторинг / скрипт импорта" autofocus
               onkeydown={(e) => { if (e.key === 'Enter') handleCreate() }}>
      </div>
      <div>
        <label class="label">Роль</label>
        <select class="input" bind:value={newRole}>
          <option value="editor">Редактор — добавление/изменение доменов, IP, категорий</option>
          <option value="viewer">Только чтение</option>
          <option value="admin">Администратор — всё, включая эндпоинты и настройки</option>
        </select>
        <p class="text-xs text-muted mt-1">Токен не может управлять другими токенами — это только из UI.</p>
      </div>
    </div>
    {#snippet footer()}
      <button class="btn btn-secondary" onclick={() => (addOpen = false)}>Отмена</button>
      <button class="btn btn-primary" onclick={handleCreate}>Создать</button>
    {/snippet}
  </Modal>
{/if}

{#if created}
  <Modal title="Токен создан" onclose={() => (created = null)} wide>
    <div class="space-y-3">
      <p class="text-sm">
        Токен <b>{created.name}</b> показывается <b>только один раз</b> — скопируйте и сохраните его. В системе хранится только хеш.
      </p>
      <div class="flex items-center gap-1 bg-bg-sunk rounded border border-bg-border px-2 py-2">
        <code class="text-xs font-mono text-blue-300 flex-1 min-w-0 break-all">{created.token}</code>
        <button class="btn-icon" onclick={() => copy(created!.token)} title="Копировать">⧉</button>
      </div>
      <div>
        <div class="label">Пример: добавить домен</div>
        <div class="flex items-start gap-1 bg-bg-sunk rounded border border-bg-border px-2 py-2">
          <pre class="text-[11px] font-mono flex-1 min-w-0 overflow-x-auto whitespace-pre">{exampleDomain(created.token)}</pre>
          <button class="btn-icon" onclick={() => copy(exampleDomain(created!.token))} title="Копировать">⧉</button>
        </div>
      </div>
    </div>
    {#snippet footer()}
      <button class="btn btn-primary" onclick={() => (created = null)}>Готово</button>
    {/snippet}
  </Modal>
{/if}

{#if helpOpen}
  <Modal title="API: добавление доменов и IP" onclose={() => (helpOpen = false)} wide>
    <div class="space-y-4 text-sm">
      <p>
        Все запросы — к <code>{base}/api/…</code> с заголовком <code>Authorization: Bearer &lt;токен&gt;</code>.
        Категорию и группу IP можно указывать по <code>key</code> (<code>category</code>, <code>group</code>) или по id (<code>categoryId</code>, <code>groupId</code>).
        Ключи смотрите во вкладках «Категории» и «MikroTik IP».
      </p>
      {#each [
        ['Добавить домен — POST /api/domains', exampleDomain('$TOKEN')],
        ['Массово — POST /api/domains/bulk', exampleBulk('$TOKEN')],
        ['Добавить IP/CIDR — POST /api/ip-ranges', exampleIp('$TOKEN')],
        ['Массово IP — POST /api/ip-ranges/bulk', exampleIpBulk('$TOKEN')]
      ] as [title, code]}
        <div>
          <div class="label">{title}</div>
          <div class="flex items-start gap-1 bg-bg-sunk rounded border border-bg-border px-2 py-2">
            <pre class="text-[11px] font-mono flex-1 min-w-0 overflow-x-auto whitespace-pre">{code}</pre>
            <button class="btn-icon" onclick={() => copy(code)} title="Копировать">⧉</button>
          </div>
        </div>
      {/each}
      <div class="text-xs text-muted space-y-1">
        <p>Ответы: <code>200</code> — создано; <code>400</code> — неверный формат / категория не найдена; <code>401</code> — неверный токен; <code>409</code> — уже существует.</p>
        <p>Справочники: <code>GET /api/categories</code>, <code>GET /api/ip-groups</code>, <code>GET /api/domains?search=…</code>, <code>GET /api/ip-ranges?group=ID</code>. Проверка токена: <code>GET /api/me</code>.</p>
        <p>Тем же токеном доступны и остальные методы API согласно роли (см. README), кроме управления токенами.</p>
      </div>
    </div>
    {#snippet footer()}
      <button class="btn btn-primary" onclick={() => (helpOpen = false)}>Закрыть</button>
    {/snippet}
  </Modal>
{/if}
